/**
 * Dreyfus-Wagner Steiner Tree algorithm.
 * Preserved from v1 — the most complex and valuable piece.
 *
 * Correctly handles: bitmask DP over terminal subsets, 0-1 BFS relaxation,
 * tree reconstruction, linearization into a token stream for display,
 * and path serialization.
 */

import { isMediaNode, isActorNode } from './constants.js';

export function popcount(x) {
  let c = 0;
  while (x) { c += x & 1; x >>= 1; }
  return c;
}

/**
 * Run the Dreyfus-Wagner Steiner tree algorithm.
 * @param {Map<string, Set<string>>} adj - adjacency map (nodeId -> Set of neighbor nodeIds)
 * @param {Set<string>} targetIdSet - set of target node IDs
 * @param {Set<string>} [excludeNodeIds] - node IDs to exclude (Tug-of-War ownership filter)
 * @returns {null | { uniqueMediaCount, tokens, allNodes, treeEdgeIds, pathString, treeNodeIds }}
 */
export function findSteinerTree(adj, targetIdSet, excludeNodeIds) {
  if (!adj || targetIdSet.size < 2) return null;

  // Build filtered adjacency
  const filteredAdj = new Map();
  for (const [id, neighbors] of adj) {
    if (excludeNodeIds && excludeNodeIds.has(id)) continue;
    const filteredNeighbors = new Set();
    for (const n of neighbors) {
      if (!excludeNodeIds || !excludeNodeIds.has(n)) {
        filteredNeighbors.add(n);
      }
    }
    filteredAdj.set(id, filteredNeighbors);
  }

  // Verify all targets exist
  for (const tid of targetIdSet) {
    if (!filteredAdj.has(tid)) return null;
  }

  const targets = [...targetIdSet];
  const k = targets.length;
  const allNodes = [...filteredAdj.keys()];
  const n = allNodes.length;
  if (n === 0) return null;

  // Index mapping
  const idx = new Map();
  allNodes.forEach((id, i) => idx.set(id, i));

  // Node weights: 0 for targets and actors, 1 for non-target media
  const w = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const id = allNodes[i];
    if (targetIdSet.has(id) || id.startsWith('a')) w[i] = 0;
    else w[i] = 1;
  }

  // Adjacency as index arrays
  const adjIdx = allNodes.map(id => [...(filteredAdj.get(id) || [])].map(nid => idx.get(nid)).filter(i => i !== undefined));

  const FULL = (1 << k) - 1;
  const INF = n + 1;

  // dp[mask][v] = min non-target media to connect terminals in mask via tree through v
  const dp = Array.from({ length: 1 << k }, () => new Int32Array(n).fill(INF));
  const from = Array.from({ length: 1 << k }, () => new Array(n).fill(null));

  // Base cases
  for (let i = 0; i < k; i++) {
    const mask = 1 << i;
    const v = idx.get(targets[i]);
    dp[mask][v] = w[v];
    from[mask][v] = { type: 0 };
  }

  // Process subsets by ascending popcount
  const masksBySize = [];
  for (let mask = 1; mask <= FULL; mask++) {
    const bits = popcount(mask);
    if (!masksBySize[bits]) masksBySize[bits] = [];
    masksBySize[bits].push(mask);
  }

  for (const masks of masksBySize) {
    if (!masks) continue;
    for (const mask of masks) {
      // Step 1: Combine two proper subsets at the same node
      for (let sub = (mask - 1) & mask; sub > 0; sub = (sub - 1) & mask) {
        const comp = mask ^ sub;
        if (comp === 0 || sub > comp) continue;
        for (let v = 0; v < n; v++) {
          if (dp[sub][v] >= INF || dp[comp][v] >= INF) continue;
          const cost = dp[sub][v] + dp[comp][v] - w[v];
          if (cost < dp[mask][v]) {
            dp[mask][v] = cost;
            from[mask][v] = { type: 1, sub, comp, v };
          }
        }
      }

      // Step 2: 0-1 BFS relaxation
      const dist = dp[mask];
      const dq = [];
      for (let v = 0; v < n; v++) { if (dist[v] < INF) dq.push(v); }
      while (dq.length > 0) {
        const u = dq.shift();
        for (const v of adjIdx[u]) {
          const nc = dist[u] + w[v];
          if (nc < dist[v]) {
            dist[v] = nc;
            from[mask][v] = { type: 2, prevNode: u };
            if (w[v] === 0) dq.unshift(v); else dq.push(v);
          }
        }
      }
    }
  }

  // Find optimal root
  let bestCost = INF, bestRoot = -1;
  for (let v = 0; v < n; v++) {
    if (dp[FULL][v] < bestCost) { bestCost = dp[FULL][v]; bestRoot = v; }
  }
  if (bestRoot < 0 || bestCost >= INF) return null;

  // Reconstruct tree
  const treeNodes = new Set();
  const treeEdgePairs = [];
  const reconstruct = (mask, v) => {
    treeNodes.add(v);
    const info = from[mask][v];
    if (!info || info.type === 0) return;
    if (info.type === 1) {
      reconstruct(info.sub, v);
      reconstruct(info.comp, v);
    } else if (info.type === 2) {
      treeEdgePairs.push([info.prevNode, v]);
      reconstruct(mask, info.prevNode);
    }
  };
  reconstruct(FULL, bestRoot);

  // Build tree adjacency for linearization
  const treeAdj = new Map();
  for (const ni of treeNodes) treeAdj.set(ni, new Set());
  for (const [u, v] of treeEdgePairs) {
    treeAdj.get(u).add(v);
    treeAdj.get(v).add(u);
  }

  // Target indices set
  const targetIdxSet = new Set(targets.map(t => idx.get(t)));

  // Pick root: leaf target at diameter endpoint
  const bfsDist = (start) => {
    const dist = new Map(); dist.set(start, 0);
    const queue = [start];
    while (queue.length > 0) {
      const u = queue.shift();
      for (const v of treeAdj.get(u) || []) {
        if (!dist.has(v)) { dist.set(v, dist.get(u) + 1); queue.push(v); }
      }
    }
    return dist;
  };
  const startTarget = idx.get(targets[0]);
  const distFromFirst = bfsDist(startTarget);
  let farthestTarget = startTarget, maxDist = 0;
  for (const ti of targetIdxSet) {
    const d = distFromFirst.get(ti) || 0;
    if (d > maxDist) { maxDist = d; farthestTarget = ti; }
  }
  const rootNode = farthestTarget;

  // Precompute subtree sizes from root
  const subtreeSize = new Map();
  const calcSize = (node, parent) => {
    let s = 1;
    for (const child of treeAdj.get(node) || []) {
      if (child !== parent) s += calcSize(child, node);
    }
    subtreeSize.set(node, s);
    return s;
  };
  calcSize(rootNode, null);

  // Linearize: produce token stream for display
  const tokens = [];
  const visited = new Set();
  const walk = (nodeIdx) => {
    visited.add(nodeIdx);
    tokens.push({ type: 'node', idx: nodeIdx });
    const children = [];
    for (const nb of treeAdj.get(nodeIdx) || []) {
      if (!visited.has(nb)) children.push(nb);
    }
    if (children.length === 0) return;
    if (children.length === 1) {
      tokens.push({ type: 'arrow' });
      walk(children[0]);
      return;
    }
    // Sort: smallest subtree first, largest last (main chain)
    children.sort((a, b) => subtreeSize.get(a) - subtreeSize.get(b));
    const mainChild = children.pop();
    tokens.push({ type: 'arrow' });
    const leafBranches = [];
    const longBranches = [];
    for (const child of children) {
      if (subtreeSize.get(child) === 1) leafBranches.push(child);
      else longBranches.push(child);
    }
    for (const lb of leafBranches) {
      visited.add(lb);
      tokens.push({ type: 'node', idx: lb });
      tokens.push({ type: 'plus' });
    }
    if (longBranches.length === 0) {
      walk(mainChild);
    } else {
      const allLong = [...longBranches, mainChild];
      for (let i = 0; i < allLong.length; i++) {
        if (i > 0) tokens.push({ type: 'plus' });
        tokens.push({ type: 'group-start' });
        walk(allLong[i]);
        tokens.push({ type: 'group-end' });
      }
    }
  };
  walk(rootNode);

  // Collect edge IDs for highlighting
  const treeEdgeIds = [];
  for (const [u, v] of treeEdgePairs) {
    const uid = allNodes[u], vid = allNodes[v];
    treeEdgeIds.push([uid, vid]);
  }

  // Collect tree node IDs
  const treeNodeIds = new Set();
  for (const ni of treeNodes) {
    treeNodeIds.add(allNodes[ni]);
  }

  // Count non-target media in optimal tree
  let mediaCount = 0;
  for (const ni of treeNodes) {
    const id = allNodes[ni];
    if (!targetIdSet.has(id) && isMediaNode(id)) mediaCount++;
  }

  // Serialize path as a compact string with branching notation
  const pathString = tokens.map(t => {
    if (t.type === 'node') return allNodes[t.idx];
    if (t.type === 'arrow') return '>';
    if (t.type === 'plus') return '+';
    if (t.type === 'group-start') return '(';
    if (t.type === 'group-end') return ')';
    return '';
  }).join('');

  return { uniqueMediaCount: mediaCount, tokens, allNodes, treeEdgeIds, treeNodeIds, pathString };
}

/** Parse a path string back into tokens */
export function parsePathString(pathString) {
  if (!pathString) return { tokens: [], allNodes: [] };
  const allNodes = [];
  const tokens = [];
  let current = '';
  for (const char of pathString) {
    if ('>+()'.includes(char)) {
      if (current) {
        const idx = allNodes.length;
        allNodes.push(current);
        tokens.push({ type: 'node', idx });
        current = '';
      }
      if (char === '>') tokens.push({ type: 'arrow' });
      else if (char === '+') tokens.push({ type: 'plus' });
      else if (char === '(') tokens.push({ type: 'group-start' });
      else if (char === ')') tokens.push({ type: 'group-end' });
    } else {
      current += char;
    }
  }
  if (current) {
    allNodes.push(current);
    tokens.push({ type: 'node', idx: allNodes.length - 1 });
  }
  return { tokens, allNodes };
}

/**
 * Run Steiner tree on an expanded adjacency (for Director's Cut).
 * Same algorithm, different input graph.
 */
export function findExpandedSteinerTree(expandedAdj, targetIdSet) {
  return findSteinerTree(expandedAdj, targetIdSet);
}
